import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { X, ArrowLeft } from "lucide-react";
import { mainCategories, iconMap } from "../../data/resourceCategories";
import { selectCategory } from "../../slices/searchSlice";
import { saveModalState, clearModalState } from "../../utils/modalStateStore";
import { openModal, closeModal } from "../../utils/modalUtils";

/**
 * Recursively finds a category by key in the nested structure
 */
const findCategoryByKey = (categories, key) => {
  for (const category of categories) {
    if (category.key === key) {
      return category;
    }
    if (category.subcategories) {
      const found = findCategoryByKey(category.subcategories, key);
      if (found) return found;
    }
  }
  return null;
};

const SubCategoryPopup = ({ 
  subcategories, 
  onClose, 
  dispatch, 
  navigate, 
  setActiveSubcategories, 
  setExpandedCard,
  initialNavigationHistory = [],
}) => {
  const { t } = useTranslation();
  const scrollYRef = useRef(0);
  
  const [navigationHistory, setNavigationHistory] = useState(() => {
    return initialNavigationHistory && initialNavigationHistory.length > 0 
      ? [...initialNavigationHistory] 
      : [];
  });
  
  const [currentSubcategories, setCurrentSubcategories] = useState(subcategories);
  
  const [navigationDirection, setNavigationDirection] = useState('forward');

  useEffect(() => {
    scrollYRef.current = openModal();
    
    return () => {
      closeModal(scrollYRef.current);
    };
  }, []);

  // Find the parent category to get title and description
  const parentKey = currentSubcategories[0]?.parentKey;
  const parentCategory = parentKey ? findCategoryByKey(mainCategories, parentKey) : null;
  const parentLabel = parentCategory ? t(parentCategory.label) : t('select_subcategory');
  const parentDescription = parentCategory?.description ? t(parentCategory.description) : '';

  const canGoBack = navigationHistory.length > 0;

  const handleBack = useCallback(() => {
    if (navigationHistory.length > 0) {
      setNavigationDirection('back');
      const newHistory = [...navigationHistory];
      const previousState = newHistory.pop();
      setNavigationHistory(newHistory);
      setCurrentSubcategories(previousState.subcategories);
    }
  }, [navigationHistory]);

  const handleSubcategoryClick = useCallback((sub) => {
    switch (sub.action) {
      case "popup":
        if (sub.subcategories) {
          setNavigationDirection('forward');
          setNavigationHistory(prev => [...prev, { 
            subcategories: currentSubcategories, 
            mainCategory: parentKey 
          }]);
          setCurrentSubcategories(sub.subcategories);
        }
        break;

      case "query":
        saveModalState({
          navigationHistory,
          currentSubcategories,
          mainCategory: parentKey,
        });
        
        const finalQueryKeyword = sub.queryKeyword || sub.key;

        if (sub.parentKey) {
          dispatch(selectCategory({ key: sub.key, parentKey: sub.parentKey, queryKeyword: finalQueryKeyword }));
          navigate(`/search/${sub.parentKey}/${sub.key}`);
        } else {
          dispatch(selectCategory({ key: sub.key, queryKeyword: finalQueryKeyword }));
          navigate(`/search/${sub.key}`);
        }
        break;

      case "navigate":
        if (sub.route) navigate(sub.route);
        break;

      case "externalLink":
        if (sub.url) window.open(sub.url, "_blank");
        break;

      case "expandedCard":
        if (sub.guideData) {
          setExpandedCard(sub.guideData);
        }
        break;

      default:
        console.error("Unknown action type:", sub.action);
    }
  }, [currentSubcategories, navigationHistory, parentKey, dispatch, navigate, setExpandedCard]);

  const handleClose = useCallback(() => {
    clearModalState();
    onClose();
  }, [onClose]);

  const contentKey = useMemo(() => {
    return currentSubcategories[0]?.parentKey || 'root';
  }, [currentSubcategories]);

  const slideVariants = {
    enter: (direction) => ({
      x: direction === 'forward' ? 30 : -30,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction) => ({
      x: direction === 'forward' ? -30 : 30,
      opacity: 0,
    }),
  };

  return (
    <>
      {/* Overlay */}
      <motion.div
        className="fixed inset-0 bg-black/50 z-[60]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={handleClose}
      />
      
      {/* Modal Container */}
      <motion.div
        className="fixed inset-0 z-[60] flex items-center justify-center p-4 pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="bg-white w-full max-w-md rounded-3xl overflow-hidden shadow-2xl pointer-events-auto"
          initial={{ scale: 0.95, y: 10 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.95, y: 10 }}
          transition={{ type: "spring", stiffness: 400, damping: 30 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`bg-primary px-5 py-4 sm:px-6 sm:py-5 flex justify-between gap-3 ${parentDescription ? 'items-start' : 'items-center'}`}>
            <div className={`flex gap-2 flex-1 min-w-0 ${parentDescription ? 'items-start' : 'items-center'}`}>
              {canGoBack && (
                <button
                  onClick={handleBack}
                  className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center 
                             rounded-full text-white hover:bg-white/20 transition-colors
                             active:scale-95"
                  aria-label={t('go_back', 'Go back')}
                >
                  <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              )}
              
              <div className="flex-1 min-w-0">
                <motion.h2 
                  key={parentLabel}
                  className="text-lg sm:text-xl font-bold text-white leading-tight"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.15 }}
                >
                  {parentLabel}
                </motion.h2>
                {parentDescription && (
                  <motion.p 
                    key={parentDescription}
                    className="text-xs sm:text-sm text-white/90 mt-1 leading-snug"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.15, delay: 0.05 }}
                  >
                    {parentDescription}
                  </motion.p>
                )}
              </div>
            </div>
            <button
              onClick={handleClose}
              className="flex-shrink-0 w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center 
                         rounded-full text-white hover:bg-white/20 transition-colors
                         active:scale-95"
              aria-label={t('close', 'Close')}
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>

          {/* Subcategories Grid */}
          <div className="p-4 sm:p-6 max-h-[60vh] overflow-y-auto overflow-x-hidden">
            <AnimatePresence mode="wait" custom={navigationDirection}>
              <motion.div 
                key={contentKey}
                className="grid grid-cols-2 gap-3 sm:gap-4"
                custom={navigationDirection}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.15, ease: "easeOut" }}
              >
                {currentSubcategories.map((sub) => {
                  const IconComponent = iconMap[sub.iconKey];
                  const hasMoreOptions = sub.action === "popup" && sub.subcategories?.length > 0;
                  
                  return (
                    <button
                      key={sub.key}
                      onClick={() => handleSubcategoryClick(sub)}
                      className="flex flex-col items-center gap-2 sm:gap-3 p-3 sm:p-4 
                                 rounded-2xl border-2 border-border 
                                 hover:border-primary hover:bg-primary/5 
                                 transition-all duration-200
                                 min-h-[100px] sm:min-h-[120px]
                                 active:scale-95 relative"
                    >
                      {/* Icon Container */}
                      <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-primary/10 
                                      flex items-center justify-center flex-shrink-0">
                        <span className="text-primary text-xl sm:text-2xl flex items-center justify-center">
                          {IconComponent}
                        </span>
                      </div>
                      {/* Label */}
                      <span className="text-xs sm:text-sm font-medium text-foreground 
                                       text-center leading-tight line-clamp-2">
                        {t(sub.label)}
                      </span>
                      
                      {hasMoreOptions && (
                        <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-primary/60" 
                              title={t('has_more_options', 'Has more options')} />
                      )}
                    </button>
                  );
                })}
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
    </>
  );
};

export default SubCategoryPopup;
